/**
 * The ellipsis marker returned by {@link paginate} in place of a page number.
 */
export const ELLIPSIS = '…' as const

export type PaginationToken = number | typeof ELLIPSIS

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** Integer range `[start, end)`, matching lodash `range`. */
function range(start: number, end: number): number[] {
  const result: number[] = []
  for (let i = start; i < end; i++) {
    result.push(i)
  }
  return result
}

/**
 * Builds the sequence of page numbers (and ellipsis markers) to render for a
 * numbered pager, keeping the window centred on the current page and collapsing
 * long gaps into a single {@link ELLIPSIS}.
 *
 * Ported from the Provet Cloud frontend `paginate()` util (which is itself based
 * on https://gist.github.com/kottenator/9d936eb3e4e3c3e02598), with the lodash
 * `clamp`/`range` dependencies inlined.
 *
 * @param currentPage 1-based current page.
 * @param pageCount Total number of pages.
 * @param pagesShown Maximum number of page tokens to show (min 7).
 * @returns An array of 1-based page numbers interleaved with `ELLIPSIS`.
 */
export function paginate(
  currentPage: number,
  pageCount: number,
  pagesShown: number = 7,
): PaginationToken[] {
  if (pageCount <= 0) {
    return []
  }

  let delta: number
  currentPage = clamp(currentPage, 1, pageCount)
  pagesShown = clamp(pagesShown, 7, pageCount)
  const centerPagesShown = pagesShown - 5
  const boundaryPagesShown = pagesShown - 3

  if (pageCount <= pagesShown) {
    delta = pagesShown
  }
  else {
    delta
      = currentPage < boundaryPagesShown || currentPage > pageCount - boundaryPagesShown
        ? boundaryPagesShown
        : centerPagesShown
  }

  const window = {
    start: Math.round(currentPage - delta / 2),
    end: Math.round(currentPage + delta / 2),
  }

  if (window.start - 1 === 1 || window.end + 1 === pageCount) {
    window.start += 1
    window.end += 1
  }

  let pages: PaginationToken[]
    = currentPage > delta
      ? range(Math.min(window.start, pageCount - delta), Math.min(window.end, pageCount) + 1)
      : range(1, Math.min(pageCount, delta + 1) + 1)

  if (currentPage > pageCount - boundaryPagesShown && pageCount > pagesShown) {
    pages = range(pageCount - delta, pageCount + 1)
  }

  const withDots = (value: number, pair: PaginationToken[]): PaginationToken[] =>
    pages.length + 1 !== pageCount ? pair : [value]
  const lastPage = pages[pages.length - 1]

  if (pages[0] !== 1) {
    pages = withDots(1, [1, ELLIPSIS]).concat(pages)
  }

  if (typeof lastPage === 'number' && lastPage < pageCount) {
    pages = pages.concat(withDots(pageCount, [ELLIPSIS, pageCount]))
  }

  return pages
}
